/**
 * SamplingFormDialog — New / Edit / Copy for Quality Control › Sampling Data.
 * Converted 1:1 from Access `frmQualityQrySamplingAddQC` (over tlc_factory.sampling).
 * 5 section bands (red labels): General · Lot's Information · Sample Sending
 * Information · PS Result · Sample Loading's / Fumigation Loading's Information —
 * each a 2-col grid (Access Left 2964→col1, 10164→col2), rows by Top.
 *
 * Required (strCheck): sample_no, sample_type, ps_date, ps_sent_date,
 * warehouse_code, lot_size_mt.
 * Readonly snapshots (Enabled=NotDefault): sample_no (auto), sale_quality,
 * contract quality des, warehouse name, cert_unit, grade_des.
 * Cascades:
 *   warehouse_code → lot_id options (TLBL/TLNC/TLNT lot_mgt; else free text)
 *   license_cert_no → cert_unit (cp_vn.short_name)
 *   tlc_contract_ref → grade (local_s.quality) [New only]
 *   sample_status contains CANCEL → status_date = today
 */
import { useEffect, useRef, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SamplingLookupItem,
  type SamplingWrite,
  useLazyGetSamplingFormQuery,
  useLazySuggestSampleNoQuery,
  useLazyCheckSampleNoQuery,
  useLazyLkSamplingCertUnitQuery,
  useLazyLkSamplingContractGradeQuery,
  useLazyLkSamplingContractSnapshotQuery,
  useCreateSamplingMutation,
  useUpdateSamplingMutation,
  useLkSamplingTlcContractsQuery,
  useLkSamplingDoNosQuery,
  useLkSamplingWarehousesQuery,
  useLkSamplingLotsQuery,
  useLkSamplingLicensesQuery,
  useLkSamplingGradesQuery,
  useLkSamplingOriginsQuery,
  useLkSamplingSampleTypesQuery,
  useLkSamplingSampleStatusesQuery,
} from '../../store/api/qualityApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { TEXT_TRUNCATE } from '@/theme/responsive'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  samplingId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  sampleNo: string
  psDate: string
  tlcContractRef: string
  doNo: string
  saleQuality: string
  saleQualityDes: string
  sampleType: string
  sampleStatus: string
  statusDate: string
  statusReason: string
  consigneeOptional: string
  warehouseCode: string
  warehouseName: string
  clientLotId: string
  lotId: string
  weightPerContMt: string
  lotSizeMt: string
  certUnit: string
  grade: string
  gradeDes: string
  licenseCertNo: string
  origin: string
  psSentDate: string
  receiver: string
  receivingAuthority: string
  inspectionBy: string
  sealNo: string
  note: string
  resultReceiptDate: string
  clientSampleRef: string
  rejectReason: string
  result: string
  loadingType: string
  note2: string
  fumigationNote: string
}

const EMPTY: Draft = {
  sampleNo: '', psDate: '', tlcContractRef: '', doNo: '', saleQuality: '', saleQualityDes: '',
  sampleType: '', sampleStatus: '', statusDate: '', statusReason: '', consigneeOptional: '',
  warehouseCode: '', warehouseName: '', clientLotId: '', lotId: '', weightPerContMt: '', lotSizeMt: '',
  certUnit: '', grade: '', gradeDes: '', licenseCertNo: '', origin: '', psSentDate: '', receiver: '',
  receivingAuthority: '', inspectionBy: '', sealNo: '', note: '', resultReceiptDate: '', clientSampleRef: '',
  rejectReason: '', result: '', loadingType: '', note2: '', fumigationNote: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const grid2Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const t = (v ?? '').toString().replace(/,/g, '').trim()
  if (!t) return null
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : null
}
/** Access Format="#,#00" (lot_size_mt / weight_per_cont_mt): thousand separator, căn phải. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))
const RESULT_OPTS: SamplingLookupItem[] = [{ value: 'Accept', label: 'Accept' }, { value: 'Reject', label: 'Reject' }]
const LOADING_OPTS: SamplingLookupItem[] = [
  { value: 'PP Bag', label: 'PP Bag' }, { value: 'New jute bag', label: 'New jute bag' },
  { value: 'Big Bag', label: 'Big Bag' }, { value: 'Bulk', label: 'Bulk' },
]

/* ── Reusable combo (Autocomplete flat "<CODE> — <NAME>", injects current value) ── */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: SamplingLookupItem[]
  loading?: boolean
  required?: boolean
  freeSolo?: boolean
  labelOnly?: boolean
  /** Field có ô name riêng kế bên (vd Warehouse Code ↔ Warehouse Name) → field
   *  combo chỉ hiện CODE; dropdown vẫn "<code> — <name>". */
  codeOnly?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: SamplingLookupItem | null) => void
  error?: boolean
}) {
  const { label, value, valueLabel, options, loading, required, freeSolo, labelOnly, codeOnly, onInputChange, onPick, error } = props
  const sel = value
    ? options.find((o) => o.value === value) ?? { value, label: valueLabel || value }
    : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  const fullLabel = (o: SamplingLookupItem): string =>
    labelOnly || o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`
  const getLabel = (o: SamplingLookupItem | string) => {
    if (typeof o === 'string') return o
    if (!o) return ''
    return codeOnly ? String(o.value) : fullLabel(o)
  }
  return (
    <Autocomplete
      size={SM}
      freeSolo={freeSolo}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => {
        if (typeof v === 'string') onPick(v, null)
        else onPick(v?.value ?? '', v)
      }}
      onInputChange={(_e, v, reason) => {
        if (freeSolo && reason === 'input') onPick(v, null)
        else if (onInputChange && reason === 'input') onInputChange(v)
      }}
      getOptionLabel={getLabel}
      renderOption={codeOnly
        ? (liProps, o) => {
            const key = typeof o === 'string' ? o : o.value
            return <li {...liProps} key={key}>{typeof o === 'string' ? o : fullLabel(o)}</li>
          }
        : undefined}
      isOptionEqualToValue={(a, b) =>
        (typeof a === 'string' ? a : a.value) === (typeof b === 'string' ? b : b.value)}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => (
        <TextField {...params} label={required ? `${label} *` : label} error={error} />
      )}
    />
  )
}

export default function SamplingFormDialog({ open, mode, samplingId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [labels, setLabels] = useState<Record<string, string>>({})
  // access-suggest: Sample No is auto/read-only → no blur. Duplicate is handled by
  // auto-regen (this Sample No was taken meanwhile → suggest a fresh one + warn).
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  // Edit/Copy fetch the record via loadForm → gate the body with a spinner until
  // hydrated so fields never flash blank. New renders immediately (no record fetch).
  const [formLoading, setFormLoading] = useState(false)
  // Race guard (skill anti #5): regen + Save click cùng tick → ref sync.
  const precheckBusyRef = useRef(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [loadForm] = useLazyGetSamplingFormQuery()
  const [suggest] = useLazySuggestSampleNoQuery()
  const [checkNo] = useLazyCheckSampleNoQuery()
  const [fetchCertUnit] = useLazyLkSamplingCertUnitQuery()
  const [fetchContractGrade] = useLazyLkSamplingContractGradeQuery()
  const [fetchContractSnapshot] = useLazyLkSamplingContractSnapshotQuery()
  const [createSampling, { isLoading: creating }] = useCreateSamplingMutation()
  const [updateSampling, { isLoading: updating }] = useUpdateSamplingMutation()
  const saving = creating || updating

  const skip = { skip: !open }
  const [scQ, setScQ] = useState('')
  const [doQ, setDoQ] = useState('')
  const tlcContracts = useLkSamplingTlcContractsQuery(scQ || undefined, skip)
  const doNos = useLkSamplingDoNosQuery(doQ || undefined, skip)
  const warehouses = useLkSamplingWarehousesQuery(undefined, skip)
  const licenses = useLkSamplingLicensesQuery(undefined, skip)
  const grades = useLkSamplingGradesQuery(undefined, skip)
  const origins = useLkSamplingOriginsQuery(undefined, skip)
  const sampleTypes = useLkSamplingSampleTypesQuery(undefined, skip)
  const sampleStatuses = useLkSamplingSampleStatusesQuery(undefined, skip)
  // lot_id options cascade off warehouse_code (TLBL/TLNC/TLNT → list; else empty → free text)
  const lotWh = draft.warehouseCode
  const isLotList = ['TLBL', 'TLNC', 'TLNT'].includes(lotWh.toUpperCase())
  const lots = useLkSamplingLotsQuery({ warehouseCode: lotWh || undefined }, { skip: !open || !isLotList })

  // Hydrate on open
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    setFormLoading(mode !== 'new') // edit/copy fetch the record → show spinner
    void (async () => {
      const sug = await suggest().unwrap().catch(() => null)
      if (cancelled) return
      if (mode === 'new') {
        setLabels({})
        setDraft({ ...EMPTY, sampleNo: sug?.sampleNo ?? '', psDate: dayjs().format('YYYY-MM-DD') })
        return
      }
      if (!samplingId) { setFormLoading(false); return }
      const f = await loadForm(samplingId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const r = f.record as unknown as Record<string, unknown>
      const g = (k: string) => s(r[k])
      const isCopy = mode === 'copy'
      setLabels({ tlcContractRef: g('buyersRef'), warehouseCode: g('whSamplingName') })
      setDraft({
        ...EMPTY,
        // Copy: brand-new auto sample_no; Edit: keep stored.
        sampleNo: isCopy ? (sug?.sampleNo ?? '') : g('sampleNo'),
        psDate: g('psDate').slice(0, 10),
        tlcContractRef: g('tlcContractRef'), doNo: g('doNo'),
        sampleType: g('sampleType'), sampleStatus: g('sampleStatus'),
        statusDate: g('statusDate').slice(0, 10), statusReason: g('statusReason'),
        consigneeOptional: g('consigneeOptional'), warehouseCode: g('warehouseCode'),
        warehouseName: g('whSamplingName'), clientLotId: g('clientLotId'), lotId: g('lotId'),
        weightPerContMt: fmtStd(g('weightPerContMt')), lotSizeMt: fmtStd(g('lotSizeMt')),
        certUnit: g('certUnit'), grade: g('grade'), licenseCertNo: g('licenseCertNo'),
        origin: g('origin'), psSentDate: g('psSentDate').slice(0, 10), receiver: g('receiver'),
        receivingAuthority: g('receivingAuthority'), inspectionBy: g('inspectionBy'), sealNo: g('sealNo'),
        note: g('note'), resultReceiptDate: g('resultReceiptDate').slice(0, 10),
        clientSampleRef: g('clientSampleRef'), rejectReason: g('rejectReason'), result: g('result'),
        loadingType: g('loadingType'), note2: g('note2'), fumigationNote: g('fumigationNote'),
      })
      setFormLoading(false)
    })()
    return () => { cancelled = true }
  }, [open, mode, samplingId, loadForm, suggest])

  // access-suggest: regen a fresh Sample No when `oldVal` is taken (force network
  // refetch — anti #7 — so we never get the cached, now-duplicate value back).
  const regenSampleNo = async (oldVal: string): Promise<boolean> => {
    const next = await suggest(undefined, false).unwrap().catch(() => null)
    const newVal = (next?.sampleNo ?? '').trim()
    if (newVal && newVal !== oldVal.trim()) {
      setDraft((p) => ({ ...p, sampleNo: newVal }))
      setRegenNotice({ from: oldVal, to: newVal })
      return true
    }
    return false
  }

  // Duplicate check (New/Copy only — sample_no auto). If the suggested code was
  // claimed meanwhile (e.g. a second tab saved first), self-heal: pull a fresh
  // code + warn. Loop converges — a unique code yields exists=false → stop.
  useEffect(() => {
    if (!open || mode === 'edit' || !draft.sampleNo) return
    let cancelled = false
    const val = draft.sampleNo
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    void (async () => {
      try {
        const res = await checkNo({ sampleNo: val }).unwrap()
        if (cancelled || !res.exists) return
        await regenSampleNo(val)
      } catch {
        /* BE raises at Save if truly duplicate — don't poison UX */
      } finally {
        if (!cancelled) { precheckBusyRef.current = false; setPrecheckBusy(false) }
      }
    })()
    return () => { cancelled = true; precheckBusyRef.current = false }
  }, [open, mode, draft.sampleNo, checkNo, suggest])

  // Auto-dismiss the regen alert once the field moves off the suggested code.
  useEffect(() => {
    if (regenNotice && draft.sampleNo.trim() !== regenNotice.to) setRegenNotice(null)
  }, [draft.sampleNo, regenNotice])

  // license_cert_no → cert_unit snapshot
  const handleLicenseChange = (value: string) => {
    setDraft((p) => ({ ...p, licenseCertNo: value }))
    if (!value) return
    void fetchCertUnit(value).unwrap()
      .then((res) => set('certUnit', res.certUnit ?? ''))
      .catch(() => undefined)
  }

  // tlc_contract_ref_Change (Access id2=0 → New/Copy, not Edit):
  //  • grade = local_s.quality for the contract
  //  • snapshot ~12 fields from the latest prior sampling row of that contract
  const handleContractChange = (value: string, item: SamplingLookupItem | null) => {
    setDraft((p) => ({ ...p, tlcContractRef: value }))
    setLabels((p) => ({ ...p, tlcContractRef: item?.label ?? '' }))
    if (mode === 'edit' || !value) return
    void fetchContractGrade(value).unwrap()
      .then((res) => { if (res.grade) set('grade', res.grade) })
      .catch(() => undefined)
    void fetchContractSnapshot(value).unwrap()
      .then((snap) => {
        setDraft((p) => ({
          ...p,
          psDate: snap.psDate ?? p.psDate,
          warehouseCode: snap.warehouseCode ?? p.warehouseCode,
          lotSizeMt: snap.lotSizeMt != null ? fmtStd(snap.lotSizeMt) : p.lotSizeMt,
          weightPerContMt: snap.weightPerContMt != null ? fmtStd(snap.weightPerContMt) : p.weightPerContMt,
          licenseCertNo: snap.licenseCertNo ?? p.licenseCertNo,
          origin: snap.origin ?? p.origin,
          psSentDate: snap.psSentDate ?? p.psSentDate,
          receiver: snap.receiver ?? p.receiver,
          receivingAuthority: snap.receivingAuthority ?? p.receivingAuthority,
          inspectionBy: snap.inspectionBy ?? p.inspectionBy,
          sealNo: snap.sealNo ?? p.sealNo,
          loadingType: snap.loadingType ?? p.loadingType,
          certUnit: snap.certUnit ?? p.certUnit,
        }))
      })
      .catch(() => undefined)
  }

  // warehouse_code change → reset lot_id when leaving list source / changing wh
  const handleWarehouseChange = (value: string, item: SamplingLookupItem | null) => {
    setDraft((p) => ({ ...p, warehouseCode: value, lotId: '', warehouseName: item?.label ?? '' }))
    setLabels((p) => ({ ...p, warehouseCode: item?.label ?? '' }))
  }

  // sample_status contains CANCEL → status_date = today
  const handleStatusChange = (value: string) => {
    const cancel = value.toUpperCase().includes('CANCEL')
    setDraft((p) => ({ ...p, sampleStatus: value, statusDate: cancel ? dayjs().format('YYYY-MM-DD') : '' }))
  }

  const req = (v: string) => !v.trim()
  // Access btnSave_Click: CANCEL status requires a Status Date.
  const cancelNeedsDate = draft.sampleStatus.toUpperCase().includes('CANCEL') && req(draft.statusDate)
  const canSave =
    canEdit && !formLoading && !saving && !precheckBusy && !cancelNeedsDate &&
    !req(draft.sampleNo) && !req(draft.sampleType) && !req(draft.psDate) &&
    !req(draft.psSentDate) && !req(draft.warehouseCode) && !req(draft.lotSizeMt)

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (skill anti #5)
    if (!canSave) return
    const payload: SamplingWrite = {
      sampleNo: draft.sampleNo || null,
      sampleType: draft.sampleType || null,
      sampleStatus: draft.sampleStatus || null,
      statusDate: draft.statusDate || null,
      statusReason: draft.statusReason || null,
      consigneeOptional: draft.consigneeOptional || null,
      tlcContractRef: draft.tlcContractRef || null,
      psDate: draft.psDate || null,
      doNo: draft.doNo || null,
      warehouseCode: draft.warehouseCode || null,
      lotId: draft.lotId || null,
      lotSizeMt: parseNum(draft.lotSizeMt),
      licenseCertNo: draft.licenseCertNo || null,
      clientLotId: draft.clientLotId || null,
      origin: draft.origin || null,
      weightPerContMt: parseNum(draft.weightPerContMt),
      certUnit: draft.certUnit || null,
      grade: draft.grade || null,
      receiver: draft.receiver || null,
      inspectionBy: draft.inspectionBy || null,
      note: draft.note || null,
      psSentDate: draft.psSentDate || null,
      receivingAuthority: draft.receivingAuthority || null,
      sealNo: draft.sealNo || null,
      resultReceiptDate: draft.resultReceiptDate || null,
      rejectReason: draft.rejectReason || null,
      clientSampleRef: draft.clientSampleRef || null,
      result: draft.result || null,
      loadingType: draft.loadingType || null,
      note2: draft.note2 || null,
      fumigationNote: draft.fumigationNote || null,
    }
    try {
      if (mode === 'edit' && samplingId) {
        await updateSampling({ id: samplingId, body: payload }).unwrap()
        snack.success(`Sampling ${payload.sampleNo} updated`)
      } else {
        await createSampling(payload).unwrap()
        snack.success(`Sampling ${payload.sampleNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      const msg = getErrorMessage(err, 'Save sampling failed')
      // access-suggest anti #13: duplicate slipped through the check→save gap
      // (e.g. another tab saved first). Regen a fresh code + warn instead of a
      // dead-end error; user just clicks Save again.
      if (mode !== 'edit' && /exist|duplicate/i.test(msg)) {
        if (await regenSampleNo(draft.sampleNo)) return
      }
      snack.error(msg)
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string, required?: boolean, errorText?: string) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: {
        size: SM, fullWidth: true, required, InputLabelProps: { shrink: true },
        error: !!errorText || (required ? req(draft[key] as string) : false),
        helperText: errorText,
      } }}
    />
  )
  const numField = (key: keyof Draft, label: string, required?: boolean) => (
    <TextField
      label={required ? `${label} *` : label} size={SM} fullWidth type="text" value={draft[key] as string}
      required={required} error={required ? req(draft[key] as string) : undefined}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={numProps}
    />
  )
  const textField = (key: keyof Draft, label: string, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, helperText?: string) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      helperText={helperText} />
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`${title} — Sample Data`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>{title} — Sample Data</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
          <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit sampling data.</Alert>}
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Sample No <b>{regenNotice.from}</b> was just created — suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        )}

        {/* ── General (T=60–1560) ── */}
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            {roField('sampleNo', 'Sample No', mode !== 'edit' && precheckBusy ? 'Verifying Sample No…' : undefined)}
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{dateField('psDate', 'PS Date', true)}</Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>
            <Combo label="TLC Contract Ref" value={draft.tlcContractRef} valueLabel={labels.tlcContractRef}
              options={tlcContracts.data?.items ?? []} loading={tlcContracts.isFetching} onInputChange={setScQ}
              onPick={handleContractChange} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>
            <Combo label="DO No" value={draft.doNo} labelOnly options={doNos.data?.items ?? []}
              loading={doNos.isFetching} onInputChange={setDoQ} onPick={(v) => set('doNo', v)} />
          </Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>
            <Combo label="Sample Type" value={draft.sampleType} labelOnly required error={req(draft.sampleType)}
              options={sampleTypes.data?.items ?? []} loading={sampleTypes.isFetching} onPick={(v) => set('sampleType', v)} />
          </Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>
            <Combo label="Sample Status" value={draft.sampleStatus} labelOnly
              options={sampleStatuses.data?.items ?? []} loading={sampleStatuses.isFetching} onPick={handleStatusChange} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 4 } }}>
            {dateField('statusDate', 'Status Date', false, cancelNeedsDate ? 'Required for cancelled status' : undefined)}
          </Box>
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 5 } }}>{textField('statusReason', 'Status Note')}</Box>

          {/* consignee_optional — Access Top=2148, last field of the General band. */}
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 6 } }}>
            {textField('consigneeOptional', 'Consignee (used if no TLC Contract Ref)')}
          </Box>
        </Box>

        {/* ── Lot's Information (T=2436) ── */}
        <Typography sx={sectionTitleSx}>Lot's Information</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            <Combo label="Warehouse Code" value={draft.warehouseCode} valueLabel={labels.warehouseCode} required codeOnly
              error={req(draft.warehouseCode)} options={warehouses.data?.items ?? []} loading={warehouses.isFetching}
              onPick={handleWarehouseChange} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{roField('warehouseName', 'Warehouse Name')}</Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>{textField('clientLotId', 'Client Lot ID')}</Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>
            {/* Access warehouse_code_AfterUpdate: TLBL/TLNC/TLNT → combo; else free text (Lot-<wh>-00000). */}
            <Combo label="Lot ID" value={draft.lotId} freeSolo={!isLotList} options={isLotList ? lots.data?.items ?? [] : []}
              loading={lots.isFetching} labelOnly onPick={(v) => set('lotId', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>{numField('weightPerContMt', 'Weight Per Cont Mt')}</Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>{numField('lotSizeMt', 'Lot Size Mt', true)}</Box>
          {/* Access: editable TextBox (auto-filled by license/contract cascade, user may override). */}
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 4 } }}>{textField('certUnit', 'Cert Unit')}</Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 5 } }}>
            <Combo label="Grade" value={draft.grade} options={grades.data?.items ?? []} loading={grades.isFetching}
              onPick={(v) => set('grade', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 5 } }}>
            <Combo label="License Cert No" value={draft.licenseCertNo} labelOnly options={licenses.data?.items ?? []}
              loading={licenses.isFetching} onPick={handleLicenseChange} />
          </Box>

          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 6 } }}>
            <Combo label="Origin" value={draft.origin} labelOnly options={origins.data?.items ?? []}
              loading={origins.isFetching} onPick={(v) => set('origin', v)} />
          </Box>
        </Box>

        {/* ── Sample Sending Information (T=4824) ── */}
        <Typography sx={sectionTitleSx}>Sample Sending Information</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{dateField('psSentDate', 'PS Sent Date', true)}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>{textField('receiver', 'Receiver')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>{textField('receivingAuthority', 'Receiving Authority')}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>{textField('inspectionBy', 'Inspection By')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>{textField('sealNo', 'Seal No')}</Box>
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 4 } }}>{textField('note', 'Note', { multiline: true, minRows: 2 })}</Box>
        </Box>

        {/* ── PS Result (T=6012) ── */}
        <Typography sx={sectionTitleSx}>PS Result</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>{dateField('resultReceiptDate', 'Result Receipt Date')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{textField('clientSampleRef', 'Client Sample Ref')}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>{textField('rejectReason', 'Reject Reason')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>
            <Combo label="Result" value={draft.result} labelOnly options={RESULT_OPTS} onPick={(v) => set('result', v)} />
          </Box>
        </Box>

        {/* ── Sample Loading's Information (T=6900) ── */}
        <Typography sx={sectionTitleSx}>Sample Loading's Information</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            <Combo label="Loading Type" value={draft.loadingType} labelOnly options={LOADING_OPTS} onPick={(v) => set('loadingType', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 2 } }}>{textField('note2', 'Note2', { multiline: true, minRows: 2 })}</Box>
        </Box>

        {/* ── Fumigation Loading's Information (T=7788) ── */}
        <Typography sx={sectionTitleSx}>Fumigation Loading's Information</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 1 } }}>{textField('fumigationNote', 'Fumigation Note', { multiline: true, minRows: 2 })}</Box>
        </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
